import React, { useCallback, useContext, useEffect, useState } from 'react';

import {
    GalleryActionContext,
    GalleryContext,
} from 'component/Accommodation/Gallery/__context';

import { useQueryEndpoint } from 'hooks/useQueryEndpoint';

import { ApiEndpoint } from 'types/Request/endpoint';
import { notSet } from 'types/tracking';

import { Category } from 'proto/Hotel/HotelMedia';

import { GalleryType } from '../types';

import { useTracking } from './useTracking';

export const useGallery = () => {
    const { hotelParams, response } = useContext(GalleryContext);
    const { trackOpen } = useTracking();
    const {
        openHotelGallery,
        updateData,
        switchType,
        switchCategory,
        setCurrentGuestRating,
        setCurrentGuestRatingImage,
    } = useContext(GalleryActionContext);

    const [showGallery, setShowGallery] = useState(false);

    const { data, isFetching } = useQueryEndpoint(
        ApiEndpoint.HotelMedia,
        {
            requestData: {
                hotelId: hotelParams?.hotelId ?? 0,
                withUnits: hotelParams?.withUnits ?? false,
            },
        },
        {
            enabled: !!hotelParams?.hotelId,
            cacheTime: 3_600_000, //does nothing as long as a query is still in used. It only kicks in as soon as the query becomes unused. After the time has passed, data will be "garbage collected" to avoid the cache from growing.
            useErrorBoundary: false,
            queryKey: [
                ApiEndpoint.HotelMedia,
                hotelParams?.hotelId,
                hotelParams?.withUnits,
            ],
        }
    );
    useEffect(() => {
        if (data && !isFetching && !response) {
            const hotelMedia = data?.hotelMedia;
            const hotelName = data?.masterData?.name ?? notSet;
            trackOpen(
                hotelParams?.hotelId ?? 0,
                hotelName,
                !!hotelMedia?.imageList?.length,
                !!hotelMedia?.guestImageList?.length,
                !!hotelMedia?.videoList?.length
            );
            updateData(data);
        }
    }, [
        data,
        hotelParams?.hotelId,
        isFetching,
        response,
        trackOpen,
        updateData,
    ]);

    const openGallery = useCallback(() => {
        hotelParams?.category && switchCategory(hotelParams?.category);
        setShowGallery(true);
    }, [hotelParams?.category, switchCategory]);

    const closeGallery = useCallback(() => {
        setShowGallery(false);
        switchType(GalleryType.Images);
        switchCategory(Category.All);
        openHotelGallery({ hotelId: 0 });
        setCurrentGuestRating();
        setCurrentGuestRatingImage();
        updateData();
    }, [
        openHotelGallery,
        setCurrentGuestRating,
        setCurrentGuestRatingImage,
        switchCategory,
        switchType,
        updateData,
    ]);

    const onOfferLinkClick = useCallback(
        (e?: React.MouseEvent) => {
            hotelParams?.callback?.(e);
            hotelParams?.closeAfterCallback && closeGallery?.();
        },
        [closeGallery, hotelParams]
    );

    return {
        isFetching,
        showGallery,
        openGallery,
        closeGallery,
        onOfferLinkClick,
    };
};
